import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { controlToastState } from "risa-oasis-ui_v2";
import { getCmmInputDataForOrder } from "../../../api/bigQuery/nycbsPharmaOrders";
import CustomTable from "../../../components/custom-table/custom-table";
import AsteraPrimaryCareSubmission from "../../../components/modals/AsteraPrimaryCareSubmission/AsteraPrimaryCareSubmission";
import CaseInitiationModal from "../../../components/modals/CaseInitiation/CaseInitiationModal";
import CaseInitiationUploadModal from "../../../components/modals/CaseInitiationUpload/CaseInitiationUploadModal";
import DocViewerModal from "../../../components/modals/docViewerModal/docViewerModalNew";
import PrimaryCarePdfModal from "../../../components/modals/primaryCarePdf/primaryCarePdfModal";
import PrimaryCareTrackStatusModal from "../../../components/modals/primaryCareTrackStatus/primaryCareTrackStatusModal";
import ReportMedicationModal from "../../../components/modals/reportMedication/reportMedication";
import ReportPrescriptionModal from "../../../components/modals/reportPrescription/reportPrescription";
import RerunConfirmModal from "../../../components/modals/rerunConfirmModal/rerunConfirmModal";
import SendToPlanConfirmModal from "../../../components/modals/sendToPlanConfirmModal/sendToPlanConfirmModal";
import { useTablesContext } from "../../../context/tablesContextProvider";
import { AestraPrimaryCareTable } from "../../../data-model/aestraPrimaryCareTable";
import { mapToCoverMyMedsInputModel } from "../../../data-model/cmmInputRequestModel";
import { FilterSection, FilterValues } from "../../../data-model/filterValues";
import { ModalId } from "../../../enums/modalId";
import { TableNames } from "../../../enums/tableNames";
import { setFilter } from "../../../redux/slice/filterValuesSlice";
import {
  closeModal,
  setOpenedModalId,
} from "../../../redux/slice/modalSliceNew";
import { AppDispatch, RootState } from "../../../redux/store/store";
import { logDataToConsole } from "../../../utils/customLogger";
import { assignRecordClick } from "../actionHandlers/assignRecordClick";
import { handlePrimaryCareButtonWithThreeDotsClick } from "../actionHandlers/buttonWithThreeDotsActionHandler";
import { viewClick } from "../actionHandlers/viewClick";
import { sendToPlanPrimaryCare } from "../apis/sendToPlanPrimaryCare";
import { useCmmScreenshot } from "../hooks/useCmmScreenshot";
import { useFilterAndSearch } from "../hooks/useFilterAndSearch";
import { useInitialDataFetchingForTable } from "../hooks/useInitialDataFetchingForTable";
import { useNextPageDataFetch } from "../hooks/useNextPageDataFetch";
import { usePrimaryCareModals } from "../hooks/usePrimaryCareModals";
import { usePrimaryCareTableData } from "../hooks/usePrimaryCareTableData";
import {
  generateTableDataForPrimaryCareTable,
  PharmaPrimaryCareTableHeader,
} from "../table/primaryCareTableData";
import { createAllFilterSections } from "../utils/createFilterSections";
import { shouldNavigateToInsuranceDetails } from "../utils/shouldNavigateToInsuranceDetails";
import { updatePrimaryCareEntry } from "../utils/updatePrimaryCareEntry";

const PrimaryCareOrdersTable = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { getTableDataFromContext, setTableDataForContext } =
    useTablesContext();

  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  const getCurrentMonthDateRange = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const startDate = `${year}${month}01`;
    const lastDay = new Date(year, now.getMonth() + 1, 0).getDate();
    const endDate = `${year}${month}${lastDay}`;
    return { startDate, endDate };
  };

  const { startDate, endDate } = getCurrentMonthDateRange();

  const pageSize = 120;

  const { primaryCareState, setPrimaryCareState, tableData, setTableData } =
    usePrimaryCareTableData();

  const {
    cmmScreenshotDownloadUrl,
    cmmScreenshotModalOpen,
    selectedCmmRowData,
    handleViewCmmClick,
    closeCmmScreenshotModal,
    externalLoading,
  } = useCmmScreenshot();

  const { initialDataFetchingExecuted, setInitialDataFetchingExecuted } =
    useInitialDataFetchingForTable(
      setPrimaryCareState,
      setTableData,
      startDate,
      endDate,
      pageSize,
    );

  const { paginationResetCount, setSearchTerm, searchTerm, validFilters } =
    useFilterAndSearch(
      initialDataFetchingExecuted,
      setTableData,
      setPrimaryCareState,
      startDate,
      endDate,
      pageSize,
    );

  const [filterData, setFilterData] = useState<FilterSection[]>([]);
  const [allDataForFilters, setAllDataForFilters] = useState<any[]>([]);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  const { setEndIndex } = useNextPageDataFetch(
    validFilters,
    searchTerm,
    initialDataFetchingExecuted,
    primaryCareState,
    setPrimaryCareState,
    setTableData,
    startDate,
    endDate,
    pageSize,
  );

  const [allPrimaryCareOrdersTableData, setAllPrimaryCareOrdersTableData] =
    useState<any[]>([]);
  const [selectedRowData, setSelectedRowData] =
    useState<AestraPrimaryCareTable | null>(null);
  const [selectedRowForSendToPlan, setSelectedRowForSendToPlan] =
    useState<AestraPrimaryCareTable | null>(null);
  const [loadingUpdateRowId, setLoadingUpdateRowId] = useState<string | null>(
    null,
  );
  const [dropdownResetKeys, setDropdownResetKeys] = useState<
    Record<string, number>
  >({});

  const {
    openedModalId,
    sendToPlanModalType,
    openRerunModal,
    rerunType,
    rerunOrderData,
    openReportPrescriptionModal,
    selectedOrderIdForReportPrescription,
    openReportMedicationModal,
    selectedOrderIdForReportMedication,
    handleSendToPlanModalOpen,
    handleSendToPlanConfirm,
    handleRerunModalOpen,
    handleRerunConfirm,
    handleReportPrescriptionModalOpen,
    handleReportMedicationModalOpen,
    closeRerunModal,
    closeReportPrescriptionModal,
    closeReportMedicationModal,
  } = usePrimaryCareModals(tableData, user?.email ?? "", () =>
    setInitialDataFetchingExecuted(false),
  );

  useEffect(() => {
    const formattedTableData = generateTableDataForPrimaryCareTable(tableData);
    const withLoading = formattedTableData.map((row) => {
      const needsLoading =
        row.identifier === loadingUpdateRowId &&
        row.update &&
        typeof row.update === "object";
      const resetKey = dropdownResetKeys[row.identifier as string];

      if (needsLoading || resetKey !== undefined) {
        return {
          ...row,
          update: {
            ...(row.update as Record<string, unknown>),
            ...(needsLoading ? { isLoading: true } : {}),
            ...(resetKey !== undefined ? { resetKey } : {}),
          },
        };
      }
      return row;
    });
    setAllPrimaryCareOrdersTableData(withLoading);

    if (
      tableData &&
      tableData.length > 0 &&
      (isFirstLoad || !validFilters || validFilters.length === 0)
    ) {
      if (isFirstLoad) {
        setIsFirstLoad(false);
      }

      setAllDataForFilters((prevData) => {
        const combinedData = [...prevData, ...tableData];
        const uniqueData = combinedData.reduce((acc, item) => {
          if (!acc.some((x: any) => x.identifier === item.identifier)) {
            acc.push(item);
          }
          return acc;
        }, [] as any[]);
        return uniqueData;
      });
    }
  }, [
    tableData,
    loadingUpdateRowId,
    dropdownResetKeys,
    validFilters,
    isFirstLoad,
  ]);

  useEffect(() => {
    if (allDataForFilters && allDataForFilters.length > 0) {
      const filters = createAllFilterSections(allDataForFilters);
      setFilterData(filters);
    }
  }, [allDataForFilters]);

  const handleSearch = (searchText: string) => {
    setSearchTerm(searchText);
  };

  const handleRefresh = () => {
    setInitialDataFetchingExecuted(false);
    setAllDataForFilters([]);
    setIsFirstLoad(true);
  };

  const navigateToForm = (rowData: any) => {
    navigate(
      `/pharma-pa-worklists/pharma-pa-questionaire/${rowData.identifier}`,
    );
  };

  const navigateToInsuranceDetails = (rowData: any) => {
    if (!shouldNavigateToInsuranceDetails(rowData)) return;
    const docId = rowData.identifier;
    if (docId) {
      navigate(`/pharma-pa-primary-care/insurance-details/${docId}`);
    }
  };

  const handleModalClose = () => {
    dispatch(closeModal());
  };

  const handleAddKeyButtonClick = (rowData: any) => {
    setSelectedRowData(rowData);
    assignRecordClick(dispatch, rowData);
  };

  const handleClinicalNoteClick = (rowData: any) => {
    setSelectedRowData(rowData);
    viewClick(dispatch);
  };

  const handleButtonWithThreeDotsOptionClick = (
    optionId: string,
    rowId: string,
  ) => {
    handlePrimaryCareButtonWithThreeDotsClick(
      optionId,
      rowId,
      (orderId, modalType = "send_to_plan") => {
        handleSendToPlanModalOpen(
          orderId,
          modalType,
          setSelectedRowForSendToPlan,
        );
      },
      (path) => navigate(path),
      handleRerunModalOpen,
      handleReportPrescriptionModalOpen,
      handleReportMedicationModalOpen,
    );
  };

  const handleTrackStatus = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      dispatch(
        setOpenedModalId({
          id: ModalId.PRIMARY_CARE_TRACK_STATUS_MODAL,
        }),
      );
    },
    [dispatch],
  );

  const handleAsteraTrackStatusSuccess = useCallback(() => {
    dispatch(closeModal());
    setInitialDataFetchingExecuted(false);
  }, [dispatch]);

  const handleFilterApply = (data: FilterValues) => {
    dispatch(setFilter(data));
  };

  const tableContextData = getTableDataFromContext(
    TableNames.PHARMA_PRIMARY_CARE_ORDERS,
  );

  useEffect(() => {
    if (tableContextData?.shouldRefetch) {
      setInitialDataFetchingExecuted(false);
      setTableDataForContext(TableNames.PHARMA_PRIMARY_CARE_ORDERS, {
        shouldRefetch: false,
      });
    }
  }, [
    tableContextData?.shouldRefetch,
    setInitialDataFetchingExecuted,
    setTableDataForContext,
  ]);

  if (primaryCareState.error) {
    console.error("Error fetching Primary Care data:", primaryCareState.error);
  }

  return (
    <div className="h-full w-full">
      <CustomTable
        tableHeaders={PharmaPrimaryCareTableHeader}
        tableData={allPrimaryCareOrdersTableData}
        itemsPerPage={10}
        pagesPerView={4}
        count={allPrimaryCareOrdersTableData.length}
        totalCount={
          primaryCareState.data?.details?.pagination?.total_count ?? 0
        }
        isFetching={primaryCareState.loading}
        tableName={TableNames.PHARMA_PRIMARY_CARE_ORDERS}
        searchingText={handleSearch}
        onReviewButtonClick={(data) => navigateToForm(data)}
        onRowClick={(data) => navigateToInsuranceDetails(data)}
        onRefreshButton={handleRefresh}
        isDisabled={primaryCareState.loading}
        showRefreshButton={true}
        showOtherButton={true}
        showFilterButton={true}
        filterSectionsData={filterData}
        onFilterApplyClicked={handleFilterApply}
        endIndexOfTable={setEndIndex}
        requestForPaginationReset={paginationResetCount}
        showInLineLoader={primaryCareState.showInlineLoader}
        onAddKeyButtonClick={handleAddKeyButtonClick}
        onViewClinicalNoteClick={handleClinicalNoteClick}
        onViewCmmClick={handleViewCmmClick}
        onButtonWithThreeDotsOptionClick={handleButtonWithThreeDotsOptionClick}
        otherButtonMeta={[
          {
            text: "Track Status",
            disabled: false,
            buttonType: "primary",
            size: "medium",
            onClick: handleTrackStatus,
          },
        ]}
        onActionOnInputClick={async (data, id, fieldId) => {
          updatePrimaryCareEntry(id, { covermymed_id: data }).catch((err) =>
            toast.error("Error updating key for row:", id, err),
          );

          (async () => {
            try {
              const orderDataResponse = await getCmmInputDataForOrder(id);
              if (!orderDataResponse?.details) return;
              const payload = mapToCoverMyMedsInputModel(
                orderDataResponse.details,
              );
              payload.cmm_result_key = data;
              const controller = new AbortController();
              await sendToPlanPrimaryCare(
                controller.signal,
                payload,
                id,
                user?.email ?? "",
              );
            } catch (err) {
              console.error("Error in send to plan for row:", id, err);
            }
          })();

          await new Promise<void>((resolve) => setTimeout(resolve, 5000));

          setTableData((prev) =>
            prev.map((row) =>
              row.identifier === id ? { ...row, cmm_result_key: data } : row,
            ),
          );
          controlToastState("astera-primary-care-submission-save-success");
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        }}
        onInputChange={(data, id) => {
          logDataToConsole(data);
          logDataToConsole(id);
        }}
        onTagDropDownSelectChange={async (value, dataId, fieldId) => {
          if (!dataId) return;
          setLoadingUpdateRowId(dataId);
          try {
            await updatePrimaryCareEntry(dataId, { case_status: value });
            setTableData((prev) =>
              prev.map((row) =>
                row.identifier === dataId
                  ? { ...row, case_status: value }
                  : row,
              ),
            );
            controlToastState("astera-primary-care-submission-save-success");
          } catch (error) {
            console.error("Update failed for row:", dataId, error);
            controlToastState("astera-primary-care-submission-save-error");
            setDropdownResetKeys((prev) => ({
              ...prev,
              [dataId]: (prev[dataId] ?? 0) + 1,
            }));
          } finally {
            setLoadingUpdateRowId(null);
          }
        }}
      />
      {openedModalId === ModalId.CASE_INITIATION_MODAL && (
        <CaseInitiationModal
          onClose={handleModalClose}
          onSave={() => setInitialDataFetchingExecuted(false)}
        />
      )}
      {openedModalId === ModalId.CASE_INITIATION_UPLOAD_MODAL && (
        <CaseInitiationUploadModal onClose={handleModalClose} />
      )}
      {openedModalId === ModalId.PRIMARY_CARE_PDF_MODAL && selectedRowData && (
        <PrimaryCarePdfModal
          onClose={handleModalClose}
          identifier={selectedRowData.identifier}
          taskHistory={
            tableData.find(
              (row) => row.identifier === selectedRowData.identifier,
            )?.task_history ?? ""
          }
          filePath={
            tableData.find(
              (row) => row.identifier === selectedRowData.identifier,
            )?.ref_doc_path ?? undefined
          }
        />
      )}
      {cmmScreenshotModalOpen && selectedCmmRowData && (
        <DocViewerModal
          fileUrl={cmmScreenshotDownloadUrl ?? ""}
          onClose={closeCmmScreenshotModal}
          type="cmm_screenshot"
          externalLoading={externalLoading}
        />
      )}
      {openedModalId === ModalId.SEND_TO_PLAN_CONFIRM_MODAL && (
        <SendToPlanConfirmModal
          onClose={handleModalClose}
          onSaveClick={() => handleSendToPlanConfirm(selectedRowForSendToPlan)}
          modalType={sendToPlanModalType}
        />
      )}
      {openRerunModal && rerunType && (
        <RerunConfirmModal
          onClose={closeRerunModal}
          onSaveClick={handleRerunConfirm}
          rerunType={rerunType as "onco-emr" | "cmm"}
          orderData={rerunOrderData}
        />
      )}
      {openReportPrescriptionModal && selectedOrderIdForReportPrescription && (
        <ReportPrescriptionModal
          onClose={closeReportPrescriptionModal}
          orderId={selectedOrderIdForReportPrescription}
        />
      )}
      {openReportMedicationModal && selectedOrderIdForReportMedication && (
        <ReportMedicationModal
          onClose={closeReportMedicationModal}
          orderId={selectedOrderIdForReportMedication}
        />
      )}
      {openedModalId === ModalId.ASTERA_PRIMARY_CARE_SUBMISSION_MODAL && (
        <AsteraPrimaryCareSubmission
          metaData={{
            data: selectedRowData,
          }}
          onClose={handleModalClose}
          onSuccess={() => setInitialDataFetchingExecuted(false)}
        />
      )}
      {openedModalId === ModalId.PRIMARY_CARE_TRACK_STATUS_MODAL && (
        <PrimaryCareTrackStatusModal
          onClose={handleAsteraTrackStatusSuccess}
          onSuccess={handleAsteraTrackStatusSuccess}
        />
      )}
    </div>
  );
};

export default PrimaryCareOrdersTable;
